import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  faLock,
  faRightToBracket,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AuthStatus, CurrentSessionData } from "../../hooks/userSession";
import { useToast } from "../../components/ui/use-toast";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { FormField } from "../../components/ui/form-field";
import { Input } from "../../components/ui/input";
import { InputGroup, InputGroupText } from "../../components/ui/input-group";
import { Label } from "../../components/ui/label";

interface EmployeeLoginProps {
  currentSession: CurrentSessionData;
}

const EmployeeLogin = ({ currentSession }: EmployeeLoginProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const isNewPasswordRequired =
    currentSession.authStatus === AuthStatus.NewPasswordRequired;

  // If already logged in, redirect to customer list
  useEffect(() => {
    if (currentSession.userSessionState.isLoggedIn) {
      navigate("/customer/list");
    }
  }, [currentSession.userSessionState.isLoggedIn, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    currentSession.login(username, password, (error) => {
      toast({
        variant: "destructive",
        title: "Acceso no permitido",
        description: "Verifica tu usuario y contraseña e intenta de nuevo.",
      });
    });
  };

  const handleNewPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Las contraseñas no coinciden",
        description: "Asegúrate de escribir la misma contraseña en ambos campos.",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        variant: "destructive",
        title: "Contraseña demasiado corta",
        description: "La contraseña debe tener al menos 8 caracteres.",
      });
      return;
    }

    currentSession.completeNewPassword(newPassword, () => {
      toast({
        variant: "destructive",
        title: "No se pudo actualizar la contraseña",
        description: "Verifica que cumpla los requisitos e intenta de nuevo.",
      });
    });
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-gradient-hero px-4 py-12">
      <Card className="w-full max-w-md shadow-medium">
        <CardHeader className="items-center space-y-3 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground shadow-soft">
            <FontAwesomeIcon icon={faLock} className="h-6 w-6" />
          </span>
          <CardTitle className="text-2xl">Portal de Empleados</CardTitle>
          <CardDescription>
            {isNewPasswordRequired
              ? "Crea tu nueva contraseña para continuar"
              : "Ingresa tus credenciales para continuar"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {isNewPasswordRequired ? (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
              <FormField>
                <Label htmlFor="newPassword">Nueva contraseña</Label>
                <InputGroup>
                  <InputGroupText className="rounded-r-none border-r-0">
                    <FontAwesomeIcon icon={faLock} />
                  </InputGroupText>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="rounded-l-none"
                    placeholder="Mínimo 8 caracteres"
                    required
                  />
                </InputGroup>
              </FormField>

              <FormField>
                <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                <InputGroup>
                  <InputGroupText className="rounded-r-none border-r-0">
                    <FontAwesomeIcon icon={faLock} />
                  </InputGroupText>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="rounded-l-none"
                    placeholder="Repite tu nueva contraseña"
                    required
                  />
                </InputGroup>
              </FormField>

              <Button type="submit" className="w-full">
                <FontAwesomeIcon icon={faRightToBracket} />
                Guardar contraseña
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField>
                <Label htmlFor="username">Usuario</Label>
                <InputGroup>
                  <InputGroupText className="rounded-r-none border-r-0">
                    <FontAwesomeIcon icon={faUser} />
                  </InputGroupText>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="rounded-l-none"
                    placeholder="Ingresa tu usuario"
                    required
                  />
                </InputGroup>
              </FormField>

              <FormField>
                <Label htmlFor="password">Contraseña</Label>
                <InputGroup>
                  <InputGroupText className="rounded-r-none border-r-0">
                    <FontAwesomeIcon icon={faLock} />
                  </InputGroupText>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-l-none"
                    placeholder="Ingresa tu contraseña"
                    required
                  />
                </InputGroup>
              </FormField>

              <Button type="submit" className="w-full">
                <FontAwesomeIcon icon={faRightToBracket} />
                Iniciar Sesión
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeLogin;
